import { ActionIcon, Badge, Box, Group, Loader, Text, Tooltip } from "@mantine/core";
import { IconHelp, IconZoom } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { MRT_ColumnDef } from "mantine-react-table";
import { Suspense } from "react";
import { ViewAllGroupsContainer } from "shared/components/organization/groups/groups";
import { USERS_TABLE_ROW_HEIGHT_PX } from "shared/const";
import { match } from "ts-pattern";
import z from "zod";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const groupTableSchema = z.object({
    id: z.string(),
    name: z.string(),
    parentsPaged: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .array(),
    childrenPaged: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .array(),
});

type GroupTableSchema = z.infer<typeof groupTableSchema>;

const NoneBadge = () => (
    <Badge color="red" variant="outline" component="button">
        None
    </Badge>
);

const HeaderTooltip = ({ header, label }: { header: string; label: string }) => {
    return (
        <Group wrap="nowrap" gap="xs">
            <Text>{header}</Text>
            <Tooltip label={label} withArrow multiline maw={300}>
                <IconHelp size={16} />
            </Tooltip>
        </Group>
    );
};

export const groupTableDefinition: MRT_ColumnDef<GroupTableSchema>[] = [
    {
        accessorKey: "name",
        header: "Name",
        size: 200,
        Cell: ({ cell }) => {
            return (
                <Text style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                    {cell.getValue<GroupTableSchema["name"]>()}
                </Text>
            );
        },
    },
    {
        accessorKey: "parentsPaged",
        header: "Parents",
        Header: (
            <HeaderTooltip
                header="Parents"
                label="Parents are groups that this group is a child of. Parents can manage things like this group's settings, membership, and sender info."
            />
        ),
        Cell: ({ cell, row, table }) => {
            const parents = cell.getValue<GroupTableSchema["parentsPaged"]>();
            const parentBadges = (
                <>
                    {parents.map((parent) => {
                        const meta = table.options.meta as { canNavigateTo: (groupId: string) => boolean };
                        const canNavigate = meta.canNavigateTo(parent.id);

                        return (
                            <Link
                                key={parent.id}
                                to="."
                                search={{ parent: { id: parent.id, name: parent.name } }}
                                params={(prev) => ({ ...prev })}
                                disabled={!canNavigate}
                            >
                                <Badge
                                    color="blue"
                                    variant="outline"
                                    component="button"
                                    style={{ cursor: canNavigate ? "pointer" : "not-allowed" }}
                                >
                                    {parent.name}
                                </Badge>
                            </Link>
                        );
                    })}
                </>
            );

            return (
                <Group h={USERS_TABLE_ROW_HEIGHT_PX} mah={USERS_TABLE_ROW_HEIGHT_PX}>
                    {match(parents.length)
                        .with(0, () => <NoneBadge />)
                        .when(
                            (len) => len < 3,
                            () => parentBadges,
                        )
                        .otherwise(() => {
                            return (
                                <Suspense fallback={<Loader />}>
                                    <ViewAllGroupsContainer id={row.original.id} mode="parents" />
                                </Suspense>
                            );
                        })}
                </Group>
            );
        },
    },
    {
        accessorKey: "childrenPaged",
        header: "Children",
        Header: (
            <HeaderTooltip
                header="Children"
                label="Children are equivalent to sub-groups. Users with permissions on a given group have those same permissions on children groups, too."
            />
        ),
        Cell: ({ cell, row }) => {
            const children = cell.getValue<GroupTableSchema["childrenPaged"]>();
            const childrenBadges = (
                <>
                    {children.map((it) => (
                        <Link key={it.id} to="." search={{ parent: { id: it.id, name: it.name } }} params={(prev) => ({ ...prev })}>
                            <Badge color="blue" variant="outline" component="button" style={{ cursor: "pointer" }}>
                                {it.name}
                            </Badge>
                        </Link>
                    ))}
                </>
            );

            return (
                <Group h={USERS_TABLE_ROW_HEIGHT_PX} mah={USERS_TABLE_ROW_HEIGHT_PX}>
                    {match(children.length)
                        .with(0, () => <NoneBadge />)
                        .when(
                            (len) => len < 3,
                            () => childrenBadges,
                        )
                        .otherwise(() => (
                            <Suspense fallback={<Loader />}>
                                <ViewAllGroupsContainer id={row.original.id} mode="children" />
                            </Suspense>
                        ))}
                </Group>
            );
        },
    },
    {
        id: "select",
        header: "Select",
        Header: <HeaderTooltip header="Select" label="Select the magnifying glass to see that group's children" />,
        columnDefType: "display",
        size: 100,
        Cell: ({ row }) => {
            return (
                <Box style={{ display: "flex", justifyContent: "center", flex: 1 }}>
                    <Link to="." search={{ parent: row.original }} params={(prev) => ({ ...prev })} tabIndex={-1}>
                        <ActionIcon variant="subtle" aria-label="View this group's children">
                            <IconZoom size={16} />
                        </ActionIcon>
                    </Link>
                </Box>
            );
        },
    },
];
